<template>
  <div
      class="tw-flex tw-flex-col tw-items-center tw-text-center tw-whitespace-normal"
      :class="[compact ? 'tw-py-6 tw-px-4' : 'tw-py-12 tw-px-6', { card }]"
  >
    <div v-if="icon || $slots.icon" class="empty-icon" :class="compact ? 'empty-icon--compact tw-mb-2' : 'tw-mb-3'">
      <div class="empty-icon__blob"></div>
      <span v-for="n in 4" :key="n" class="empty-icon__spark" :class="`empty-icon__spark--${n}`">
        <svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 0c.8 6.5 5.5 11.2 12 12-6.5.8-11.2 5.5-12 12-.8-6.5-5.5-11.2-12-12C6.5 11.2 11.2 6.5 12 0z"/></svg>
      </span>
      <div
          class="empty-icon__tile tw-flex tw-items-center tw-justify-center tw-rounded-2xl tw-bg-gradient-to-br tw-from-primary/15 tw-to-primary/5 tw-text-primary"
          :class="compact ? 'tw-w-10 tw-h-10 tw-text-lg' : 'tw-w-14 tw-h-14 tw-text-2xl'"
      >
        <slot name="icon"><i :class="icon"></i></slot>
      </div>
    </div>

    <h5 class="tw-m-0 tw-font-semibold tw-text-gray-800" :class="compact ? 'tw-text-sm' : 'tw-text-base'">
      {{ title || $t('errors.notFound') }}
    </h5>
    <p v-if="subtitle" class="tw-mt-1 tw-mb-0 tw-max-w-md tw-text-sm tw-leading-relaxed tw-text-gray-500">
      {{ subtitle }}
    </p>

    <div v-if="btnText || $slots.default" class="tw-mt-5 tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
      <b-button v-if="btnText" variant="primary" @click="$emit('click')">
        <i v-if="btnIcon" :class="btnIcon" class="tw-mr-1"></i>{{ btnText }}
      </b-button>
      <slot/>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    title: String,
    // every empty state gets a one-line subtitle; title-only ones read as unfinished
    subtitle: {
      type: String,
      required: true
    },
    // feather class, or use the #icon slot for any other icon; pass an empty string to drop it
    icon: {
      type: String,
      default: 'fe-inbox'
    },
    btnText: String,
    btnIcon: {
      type: String,
      default: 'fe-plus'
    },
    // page-level empty state on its own white card
    card: {
      type: Boolean,
      default: false
    },
    // inside modals, tabs and panels
    compact: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style lang="scss" scoped>
// spark sizes are in em, so the compact variant only shrinks the font-size
.empty-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 96px;
  font-size: 12px;

  &--compact {
    width: 88px;
    height: 68px;
    font-size: 9px;
  }
}

.empty-icon__blob {
  position: absolute;
  width: 70%;
  height: 79%;
  background: rgba($color_primary, 0.08);
  animation: empty-morph 7s ease-in-out infinite;
}

.empty-icon__tile {
  position: relative;
  animation: empty-tilt 3.2s ease-in-out infinite;
}

.empty-icon__spark {
  position: absolute;
  width: 1em;
  height: 1em;
  color: $color_primary;
  opacity: 0;
  animation: empty-twinkle 2.4s ease-in-out infinite;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  &--1 { top: 6%; right: 18%; }
  &--2 { bottom: 12%; left: 13%; width: 0.75em; height: 0.75em; color: $color_primary_muted; animation-delay: 0.8s; }
  &--3 { top: 19%; left: 18%; width: 0.6em; height: 0.6em; animation-delay: 1.6s; }
  &--4 { bottom: 4%; right: 15%; width: 0.65em; height: 0.65em; color: $color_primary_muted; animation-delay: 1.2s; }
}

@keyframes empty-morph {
  0%, 100% { border-radius: 42% 58% 55% 45% / 48% 42% 58% 52%; transform: rotate(0deg); }
  50% { border-radius: 58% 42% 45% 55% / 55% 58% 42% 45%; transform: rotate(12deg) scale(1.05); }
}

@keyframes empty-tilt {
  0%, 100% { transform: rotate(-4deg) translateY(1px); }
  50% { transform: rotate(4deg) translateY(-3px); }
}

@keyframes empty-twinkle {
  0%, 100% { opacity: 0; transform: scale(0.3) rotate(0deg); }
  40%, 60% { opacity: 1; transform: scale(1) rotate(45deg); }
}

@media (prefers-reduced-motion: reduce) {
  .empty-icon__blob, .empty-icon__tile { animation: none; }
  .empty-icon__spark { animation: none; opacity: 0.8; }
}
</style>
